/**
 * RealtimeTransport — WS-first com fallback automático para SSE (Story E1.5.2).
 *
 * Resolve o bug recorrente de WebSocket Cloudflare instável em prod
 * (MEMORY `f82c424e`). Quando o WS falha 3 vezes em <30s, o transport
 * comuta para Server-Sent Events (endpoint backend `/api/v1/sse/notifications`
 * inaugurado pela Story E1.5.1) — unidirecional sobre HTTP/1.1, sobrevive
 * a proxies/WAFs hostis a upgrades WebSocket.
 *
 * Trade-offs intencionais
 * -----------------------
 *
 * 1. **SSE via `fetch` streaming**, não via `EventSource`. Motivo: o endpoint
 *    SSE requer `Authorization: Bearer ...` header; `EventSource` nativo do
 *    browser não permite custom headers. Adotamos o padrão usado por
 *    OpenAI client / LangChain.js / Vercel AI SDK: `fetch` + `ReadableStream`
 *    + parser SSE manual. Zero deps novas (NFR-I1).
 *
 * 2. **O SSE se recupera e volta ao WS (parte B, 30/09/2026).** A versão
 *    original ficava em SSE até recarregar a página, e o SSE que caía (deploy,
 *    proxy) não se reconectava: a aba ficava muda até o token mudar — e um
 *    deploy derruba o WS 3 vezes em menos de 30 s, justamente o gatilho do
 *    fallback. Agora o SSE reconecta sozinho (2 s dobrando até 30 s, mais até
 *    1 s de variação para as abas não voltarem juntas) e, a cada 60 s, uma
 *    sonda tenta o WS: se abrir, a aba volta ao WS. O vaivém
 *    fica limitado a um ciclo por minuto — a sonda só promove um WS que abriu.
 *    A sonda fica armada durante TODO o regime SSE, também com o SSE
 *    reconectando: se ela só rodasse com o stream aberto, um SSE que falha de
 *    vez (handshake com erro, ou 200 que fecha na hora) deixaria a aba sem nada
 *    ao vivo até o token girar, mesmo com o WS de volta.
 *    A espera só volta a 2 s se o stream ficou de pé ao menos 30 s: um 200 que
 *    fecha na hora (backend sem Redis) continua dobrando a espera.
 *
 * 3. **Janela de falha deslizante de 30s**. Conta falhas em janela móvel: ao
 *    primeiro erro, registra timestamp; se 3+ falhas acontecem em ≤30s desde
 *    o primeiro, vira o transporte. Se a 4ª falha vier 31s após a 1ª, o
 *    contador zera (rede oscilou, mas WS ainda é viável).
 *
 * 4. **Reconnect WS a cada 3 s** até a janela encher; após 3 falhas, fallback.
 *
 * 5. **Sem `sseUrl` não há reserva SSE (portal do parceiro, D3 do passo 19).** O
 *    SSE é 403 para o parceiro, então o transporte nunca cai para ele: o WS reabre
 *    com recuo 3 s → 6 → 12 → 24 → 30 s (zerado quando o socket abre). `wsUrl`
 *    pode ser função (lida a cada tentativa, inclusive assíncrona: o token vence em
 *    15 min), `aoFechar` recebe o código de fechamento e decide "reconectar" ou
 *    "parar", e `enviar()` manda a presença da aba.
 *
 * Estados
 * -------
 * - `connecting` — tentando WS, ou reconectando o SSE
 * - `ws` — WebSocket aberto
 * - `sse` — stream SSE aberto (só com o stream de pé)
 * - `disconnected` — `.disconnect()` chamado pelo consumidor (cleanup)
 *
 * Uso
 * ---
 * ```ts
 * const t = new RealtimeTransport({
 *   wsUrl: 'wss://api.example/ws/notifications?token=...',
 *   sseUrl: 'https://api.example/api/v1/sse/notifications',
 *   getAuthToken: () => localStorage.getItem('token'),
 *   onEvent: (data) => console.log('event', data),
 *   onModeChange: (mode) => console.log('mode now', mode),
 * });
 * await t.connect();
 * // ... later
 * t.disconnect();
 * ```
 */

export type TransportMode = "connecting" | "ws" | "sse" | "disconnected";

/** Endereço do WS: texto, ou função (lida a CADA tentativa, pode ser assíncrona: o token do portal vence em 15 min). */
export type EntregaDaUrl = string | (() => string | Promise<string>);

/** O que o servidor disse ao fechar o WS. `code` 1006 = sem código: o aperto de mão foi recusado (fechar antes do `accept` vira HTTP 403) ou a rede caiu. */
export interface InfoDoFechamento {
  code: number;
  reason: string;
}

export type DecisaoDoFechamento = "reconectar" | "parar";

export interface RealtimeTransportOptions {
  /** WebSocket URL completa (já com `?token=...` na query se aplicável). Vazia (ou função que devolve vazio) = ainda não dá: conta como falha e tenta de novo. */
  wsUrl: EntregaDaUrl;
  /** URL HTTP(S) do endpoint SSE backend. Sem ela NÃO há reserva SSE (o portal do parceiro: o SSE é 403 para ele). */
  sseUrl?: string;
  /** Getter do JWT — chamado a cada conexão SSE (token pode rotacionar). */
  getAuthToken?: () => string | null;
  /** Callback para cada evento recebido (já parseado de JSON). */
  onEvent: (data: unknown) => void;
  /** Callback para transições de modo (notifica store global). */
  onModeChange?: (mode: TransportMode) => void;
  /** Número de falhas WS na janela antes de cair pra SSE. Default: 3. */
  failureThreshold?: number;
  /** Janela móvel de tempo (ms) para contar falhas. Default: 30000 (30s). */
  failureWindowMs?: number;
  /**
   * Chamado a cada fechamento do WS, com o código do servidor. Devolve o que fazer: "reconectar" (a espera de sempre)
   * ou "parar" (o modo vira `disconnected` e o transporte não tenta mais). Pode ser assíncrono (renovar o token antes
   * de reabrir). Sem ele, o comportamento é o de sempre: reabre.
   */
  aoFechar?: (info: InfoDoFechamento) => DecisaoDoFechamento | Promise<DecisaoDoFechamento>;
}

const DEFAULT_FAILURE_THRESHOLD = 3;
const DEFAULT_FAILURE_WINDOW_MS = 30_000;

/** Primeira espera antes de reconectar o SSE; dobra a cada falha seguida. */
export const SSE_ESPERA_INICIAL_MS = 2_000;
/** Teto da espera do SSE. */
export const SSE_ESPERA_MAXIMA_MS = 30_000;
/** Variação aleatória somada à espera (evita todas as abas voltando juntas). */
export const SSE_VARIACAO_MAXIMA_MS = 1_000;
/** No regime SSE (aberto ou reconectando), de quanto em quanto tempo uma sonda tenta voltar ao WS. */
export const SONDA_WS_MS = 60_000;

/** Espera antes de reabrir o WS depois de um fechamento (e a base do recuo quando não há SSE). */
export const ESPERA_DE_REABERTURA_MS = 3_000;
/** Teto do recuo do WS quando não há reserva SSE. */
export const ESPERA_MAXIMA_SEM_SSE_MS = 30_000;
/** `WebSocket.OPEN`: o dublê dos testes não traz a constante estática. */
const WS_ABERTO = 1;

export class RealtimeTransport {
  private mode: TransportMode = "disconnected";
  private wsFailures = 0;
  private wsFailureWindowStart = 0;
  private ws: WebSocket | null = null;
  private sseAbort: AbortController | null = null;
  private disposed = false;
  /** Em regime SSE (aberto ou reconectando): `connect()` não reabre o WS; a sonda fica armada. */
  private emSse = false;
  private sseFalhasSeguidas = 0;
  /** Quando o stream da tentativa corrente abriu (200); `null` se ainda não abriu. */
  private sseAbertoEm: number | null = null;
  private sseTimer: ReturnType<typeof setTimeout> | null = null;
  private sondaTimer: ReturnType<typeof setTimeout> | null = null;
  private sonda: WebSocket | null = null;
  /** O consumidor mandou parar (`aoFechar` → "parar"): `connect()` vira no-op e nada reabre. */
  private parado = false;
  /** Quantas vezes o WS fechou/falhou sem chegar a abrir; zera no `onopen`. Só dá o recuo quando não há SSE. */
  private falhasSeguidas = 0;

  private readonly opts: Required<
    Pick<RealtimeTransportOptions, "failureThreshold" | "failureWindowMs">
  > &
    RealtimeTransportOptions;

  constructor(opts: RealtimeTransportOptions) {
    this.opts = {
      failureThreshold: DEFAULT_FAILURE_THRESHOLD,
      failureWindowMs: DEFAULT_FAILURE_WINDOW_MS,
      ...opts,
    };
  }

  /** Modo corrente. Para reatividade em React, use `onModeChange`. */
  getMode(): TransportMode {
    return this.mode;
  }

  /** Inicia conexão pelo WS. Em regime SSE não faz nada (quem volta é a sonda). */
  async connect(): Promise<void> {
    if (this.disposed || this.parado) return;
    if (this.emSse) return;
    this.setMode("connecting");
    this.openWebSocket();
  }

  /** Fecha tudo. Após chamar, `connect()` será no-op até nova instância. */
  disconnect(): void {
    this.disposed = true;
    this.cancelarReconexaoSse();
    this.cancelarSonda();
    this.fecharSonda();
    this.closeWebSocket();
    this.closeSSE();
    this.setMode("disconnected");
  }

  // ── WebSocket ─────────────────────────────────────────────────────

  private openWebSocket(): void {
    this.resolverUrlDoWs((url) => {
      // Durante a espera do endereço (a renovação do token) o consumidor pode ter desconectado ou mandado parar.
      if (this.disposed || this.parado || this.emSse) return;
      if (!url) {
        // Sem endereço agora (o token do portal não pôde ser renovado, por exemplo): conta como uma falha de abertura.
        this.recordWsFailure();
        if (this.shouldFallbackToSSE()) {
          this.openSSE();
          return;
        }
        this.setMode("connecting");
        this.agendarReabertura();
        return;
      }
      try {
        const ws = new WebSocket(url);
        this.ws = ws;
        this.ligarWebSocket(ws);
      } catch (err) {
        // `new WebSocket()` síncrono pode levantar (URL inválida etc.) —
        // tratado como falha imediata, igual a um `onerror`.
        console.error("[RealtimeTransport] WS construction failed:", err);
        this.recordWsFailure();
        if (this.shouldFallbackToSSE()) {
          this.openSSE();
        } else if (!this.opts.sseUrl) {
          // Sem SSE de reserva ninguém mais tentaria: reabre com recuo.
          this.agendarReabertura();
        }
      }
    });
  }

  /** Entrega o endereço do WS a `aoTer`: na hora se `wsUrl` é texto (ou função síncrona), depois da promessa se não. Erro vira "". */
  private resolverUrlDoWs(aoTer: (url: string) => void): void {
    const alvo = this.opts.wsUrl;
    if (typeof alvo === "string") {
      aoTer(alvo);
      return;
    }
    let valor: string | Promise<string>;
    try {
      valor = alvo();
    } catch (err) {
      console.error("[RealtimeTransport] wsUrl falhou:", err);
      aoTer("");
      return;
    }
    if (typeof valor === "string") {
      aoTer(valor);
      return;
    }
    void valor.then(aoTer, (err) => {
      console.error("[RealtimeTransport] wsUrl falhou:", err);
      aoTer("");
    });
  }

  /** O WS fechou: pergunta ao consumidor (se há `aoFechar`) o que fazer; sem ele, reabre como sempre. */
  private decidirReabertura(info: InfoDoFechamento): void {
    const { aoFechar } = this.opts;
    if (!aoFechar) {
      this.agendarReabertura();
      return;
    }
    let decisao: DecisaoDoFechamento | Promise<DecisaoDoFechamento>;
    try {
      decisao = aoFechar(info);
    } catch (err) {
      console.error("[RealtimeTransport] aoFechar falhou:", err);
      decisao = "reconectar";
    }
    void Promise.resolve(decisao)
      .catch((err): DecisaoDoFechamento => {
        console.error("[RealtimeTransport] aoFechar falhou:", err);
        return "reconectar";
      })
      .then((d) => {
        if (this.disposed || this.emSse || this.parado) return;
        if (d === "parar") {
          this.parar();
          return;
        }
        this.agendarReabertura();
      });
  }

  private agendarReabertura(): void {
    const espera = this.esperaDeReabertura();
    this.falhasSeguidas += 1;
    window.setTimeout(() => {
      if (!this.disposed && !this.parado && !this.emSse && this.mode === "connecting") {
        this.openWebSocket();
      }
    }, espera);
  }

  private esperaDeReabertura(): number {
    // Com SSE de reserva é a janela de falhas (3 em 30 s) que decide; a espera é a de sempre.
    if (this.opts.sseUrl) return ESPERA_DE_REABERTURA_MS;
    // Sem SSE o recuo é o que protege o servidor (a variação evita as abas voltando juntas depois de um deploy).
    const base = Math.min(
      ESPERA_MAXIMA_SEM_SSE_MS,
      ESPERA_DE_REABERTURA_MS * 2 ** this.falhasSeguidas
    );
    return base + Math.floor(Math.random() * SSE_VARIACAO_MAXIMA_MS);
  }

  /** O consumidor decidiu parar: fecha tudo e fica em `disconnected` (diferente de `disconnect()`, que descarta a instância). */
  private parar(): void {
    this.parado = true;
    this.cancelarSonda();
    this.fecharSonda();
    this.closeWebSocket();
    this.setMode("disconnected");
  }

  /** Manda uma mensagem ao servidor pelo WS aberto. `false` se não há socket aberto (nada é guardado para depois). */
  enviar(mensagem: unknown): boolean {
    const ws = this.ws;
    if (!ws || ws.readyState !== WS_ABERTO) return false;
    try {
      ws.send(typeof mensagem === "string" ? mensagem : JSON.stringify(mensagem));
      return true;
    } catch {
      return false;
    }
  }

  /** Handlers do WS de verdade (o aberto por `connect` ou a sonda promovida). */
  private ligarWebSocket(ws: WebSocket): void {
    ws.onopen = () => {
      this.wsFailures = 0;
      this.wsFailureWindowStart = 0;
      this.falhasSeguidas = 0;
      this.setMode("ws");
    };

    ws.onmessage = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        this.opts.onEvent(data);
      } catch (err) {
        console.error("[RealtimeTransport] WS message parse error:", err);
      }
    };

    ws.onerror = () => {
      // `onerror` sempre é seguido por `onclose` — só recorda falha aqui;
      // a decisão de fallback acontece em `onclose`.
      this.recordWsFailure();
    };

    ws.onclose = (ev?: CloseEvent) => {
      if (this.ws === ws) this.ws = null;
      if (this.disposed || this.parado) return;
      if (this.shouldFallbackToSSE()) {
        this.openSSE();
        return;
      }
      if (this.emSse) return;
      this.setMode("connecting");
      this.decidirReabertura({
        code: typeof ev?.code === "number" ? ev.code : 0,
        reason: typeof ev?.reason === "string" ? ev.reason : "",
      });
    };
  }

  private closeWebSocket(): void {
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* noop */
      }
      this.ws = null;
    }
  }

  private recordWsFailure(): void {
    const now = Date.now();
    if (this.wsFailures === 0 || now - this.wsFailureWindowStart > this.opts.failureWindowMs) {
      // Primeira falha OU janela expirou → reset
      this.wsFailures = 1;
      this.wsFailureWindowStart = now;
    } else {
      this.wsFailures += 1;
    }
  }

  private shouldFallbackToSSE(): boolean {
    return (
      Boolean(this.opts.sseUrl) &&
      this.wsFailures >= this.opts.failureThreshold &&
      Date.now() - this.wsFailureWindowStart <= this.opts.failureWindowMs
    );
  }

  // ── Sonda: no regime SSE, tenta voltar ao WS ─────────────────────

  /**
   * Arma a sonda se ainda não há uma armada nem uma em curso. Idempotente: entrar
   * de novo no regime SSE (o WS promovido que cai outra vez) não reinicia o
   * relógio, e nunca há duas sondas juntas.
   */
  private armarSonda(): void {
    if (this.sondaTimer !== null || this.sonda !== null) return;
    this.agendarSonda();
  }

  private agendarSonda(): void {
    this.cancelarSonda();
    this.sondaTimer = setTimeout(() => {
      this.sondaTimer = null;
      this.sondarWs();
    }, SONDA_WS_MS);
  }

  private cancelarSonda(): void {
    if (this.sondaTimer !== null) {
      clearTimeout(this.sondaTimer);
      this.sondaTimer = null;
    }
  }

  private fecharSonda(): void {
    if (this.sonda) {
      const sonda = this.sonda;
      this.sonda = null;
      try {
        sonda.close();
      } catch {
        /* noop */
      }
    }
  }

  private sondarWs(): void {
    if (this.disposed || !this.emSse || this.sonda !== null) return;
    this.resolverUrlDoWs((url) => {
      if (this.disposed || !this.emSse || this.sonda !== null) return;
      if (!url) {
        this.agendarSonda();
        return;
      }
      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch {
        this.agendarSonda();
        return;
      }
      this.sonda = ws;
      ws.onopen = () => {
        if (this.disposed || this.sonda !== ws) {
          try {
            ws.close();
          } catch {
            /* noop */
          }
          return;
        }
        // Voltou: a sonda vira o WS de verdade e o SSE é largado.
        this.sonda = null;
        this.emSse = false;
        this.sseFalhasSeguidas = 0;
        this.cancelarReconexaoSse();
        this.closeSSE();
        this.wsFailures = 0;
        this.wsFailureWindowStart = 0;
        this.ws = ws;
        this.ligarWebSocket(ws);
        this.setMode("ws");
      };
      ws.onerror = () => {
        /* a sonda não conta falha: o SSE segue de pé */
      };
      ws.onclose = () => {
        if (this.sonda !== ws) return;
        this.sonda = null;
        if (!this.disposed && this.emSse) this.agendarSonda();
      };
    });
  }

  // ── SSE (via fetch streaming) ─────────────────────────────────────

  private openSSE(): void {
    if (this.disposed) return;
    this.emSse = true;
    this.closeWebSocket();
    // A sonda vale para o regime inteiro, não só para o stream aberto: com o SSE
    // falhando sempre, é ela que traz a aba de volta quando o WS voltar.
    this.armarSonda();
    this.abrirStreamSse();
  }

  private abrirStreamSse(): void {
    if (this.disposed || !this.emSse) return;
    this.closeSSE();

    const token = this.opts.getAuthToken?.() ?? null;
    const controller = new AbortController();
    this.sseAbort = controller;
    // Cada tentativa começa sem "aberto em": a falha de handshake não herda o de uma anterior.
    this.sseAbertoEm = null;

    const headers: Record<string, string> = {
      Accept: "text/event-stream",
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    void this.streamSSE(controller, headers);
  }

  private async streamSSE(
    controller: AbortController,
    headers: Record<string, string>
  ): Promise<void> {
    try {
      // só se entra no regime SSE com sseUrl (shouldFallbackToSSE)
      const response = await fetch(this.opts.sseUrl as string, {
        method: "GET",
        headers,
        signal: controller.signal,
        credentials: "include",
      });

      if (!response.ok || !response.body) {
        console.error(
          "[RealtimeTransport] SSE handshake failed:",
          response.status,
          response.statusText
        );
        this.agendarReconexaoSse(controller);
        return;
      }

      // Stream de pé: agora sim o modo é `sse`. A espera NÃO zera aqui: o backend
      // responde 200 e fecha quando o Redis cai, e zerar a cada 200 faria a aba
      // bater no servidor a cada ~2 s. Só zera quando o stream prova estabilidade.
      this.sseAbertoEm = Date.now();
      this.setMode("sse");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (!controller.signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Eventos SSE são separados por linha em branco (\n\n).
        let sepIdx: number;
        while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          this.parseAndDispatch(rawEvent);
        }
      }
      if (controller.signal.aborted) return;
      // O servidor fechou (deploy, proxy): reconecta.
      this.agendarReconexaoSse(controller);
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error("[RealtimeTransport] SSE stream error:", err);
      this.agendarReconexaoSse(controller);
    }
  }

  private agendarReconexaoSse(controller: AbortController): void {
    if (this.disposed || !this.emSse || controller.signal.aborted) return;
    if (this.sseAbort !== controller) return; // já substituído
    // A sonda NÃO é cancelada aqui: ela segue armada durante a reconexão.
    this.setMode("connecting");
    // Um stream que ficou de pé pelo menos o teto da espera foi estável: a queda
    // depois disso é um problema novo e volta à espera inicial.
    if (this.sseAbertoEm !== null && Date.now() - this.sseAbertoEm >= SSE_ESPERA_MAXIMA_MS) {
      this.sseFalhasSeguidas = 0;
    }
    const base = Math.min(
      SSE_ESPERA_MAXIMA_MS,
      SSE_ESPERA_INICIAL_MS * 2 ** this.sseFalhasSeguidas
    );
    const espera = base + Math.floor(Math.random() * SSE_VARIACAO_MAXIMA_MS);
    this.sseFalhasSeguidas += 1;
    this.cancelarReconexaoSse();
    this.sseTimer = setTimeout(() => {
      this.sseTimer = null;
      this.abrirStreamSse();
    }, espera);
  }

  private cancelarReconexaoSse(): void {
    if (this.sseTimer !== null) {
      clearTimeout(this.sseTimer);
      this.sseTimer = null;
    }
  }

  private parseAndDispatch(rawEvent: string): void {
    // Cada evento é múltiplas linhas: `event: x`, `data: ...`, `id: ...`.
    // Ignoramos eventos que são só ping (linhas iniciadas por `:`).
    const lines = rawEvent.split("\n");
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith(":")) continue; // ping/comment
      if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
    if (dataLines.length === 0) return;
    const payload = dataLines.join("\n");
    try {
      const parsed = JSON.parse(payload);
      this.opts.onEvent(parsed);
    } catch (err) {
      console.error("[RealtimeTransport] SSE payload parse error:", err);
    }
  }

  private closeSSE(): void {
    if (this.sseAbort) {
      this.sseAbort.abort();
      this.sseAbort = null;
    }
  }

  // ── Mode bookkeeping ──────────────────────────────────────────────

  private setMode(next: TransportMode): void {
    if (this.mode === next) return;
    this.mode = next;
    this.opts.onModeChange?.(next);
  }
}

export default RealtimeTransport;
